import { Buffer } from "buffer"
import { getRoleAssumption } from "./roleAssumptionStore"
import { getPlayerSessionToken } from "./playerSessionStore"
import { claimRoles, claimOrgId, claimIdpOrgId, claimPermissions, claimIsPasswordlessUser } from "../context/auth/claims"

/**
 * Authentication Utilities
 * For Auth0 / Hasura conventions (i.e. IDP + REST API)
 *
 * During an active manager role-assumption session, the role and org
 * helpers below return the *assumed* role/org — the rest of the app
 * gates features as if the manager were natively that role. The
 * manager's identity (email, picture, sub) is unchanged: writes are
 * still attributed to the real manager on the server.
 *
 * Note: `isManager` intentionally never returns true while assuming
 * another role (assuming `manager` is blocked server-side), so
 * manager-only UI (assume-role menu, Admin Console) hides correctly
 * during a session.
 */

export const userImage = (user) => {
  return user?.picture ?? null
}

// Whether this subject signed in without a password.
//
// Auth0 encodes it in the subject prefix ("email|..."); Keycloak subjects are bare
// UUIDs with no such signal, so the server stamps an `authPlane` attribute instead.
// claimIsPasswordlessUser understands both. Presentation only — never authorisation.
export const isEmailUser = (user) => claimIsPasswordlessUser(user)

export const userEmail = (user) => {
  return user?.email ?? null
}

const userIsRole = ({ user, role }) => claimRoles(user).includes(role)

// Use a single getRoleAssumption() read per call so the active/role-comparison
// pair always sees the same snapshot — also avoids a redundant sessionStorage
// read on every isSponsor/isTrainer invocation (these helpers run on most renders).
export const isSponsor = (user) => {
  const assumption = getRoleAssumption()
  if (assumption) return assumption.assumed?.role === "sponsor"
  return userIsRole({ user, role: "sponsor" }) || userIsRole({ user, role: "manager" })
}

export const isManager = (user) => {
  // Assuming `manager` is blocked server-side; treat any active session
  // as definitely-not-manager so manager-only UI hides while assumed.
  if (getRoleAssumption()) return false
  return userIsRole({ user, role: "manager" })
}

export const isTrainer = (user) => {
  const assumption = getRoleAssumption()
  if (assumption) return assumption.assumed?.role === "trainer"
  return userIsRole({ user, role: "trainer" })
}

export const userOrg = (user) => {
  const assumption = getRoleAssumption()
  if (assumption) return assumption.assumed?.orgId ?? ""
  return claimOrgId(user)
}

// The IdP's OWN org identifier (Auth0 `org_xxx`). Empty on Keycloak, where orgs are
// modelled as a user attribute rather than a Keycloak Organization — callers use it
// only to pass org context to /authorize and skip that when it is absent.
export const userAuth0OrgId = (user) => claimIdpOrgId(user)

// Per-user feature grants ("feature:action:scope").
//
// Native sessions: read the id-token permissions claim (emitted by the Auth0
// id-token-setup action from app_metadata.permissions).
//
// ITF-10: during a manager role-assumption session the Auth0 user object still holds the
// MANAGER's claim, which has no `organizations:*`/`scenarios:*` grants (managers get their
// power from the isManager short-circuit, not from permission grants). Since isManager
// intentionally returns false while assuming, the permission-derived gates
// (canManageOrgs / canManageChildOrgs / canEditScenarioOrgAccess) would evaluate the wrong
// grant set and hide org management. The assumed session carries the target role's grants
// in the assumption store (stamped server-side per target role + org partner status), so
// prefer those while an assumption is active — this makes the assumed session behave
// exactly like a native member of the target org.
export const userPermissions = (user) => {
  const assumption = getRoleAssumption()
  if (assumption) return assumption.assumed?.permissions ?? []
  return claimPermissions(user)
}

// True when running inside the CTIR app-shell iframe (?embedded=1). Sticky via
// sessionStorage so internal route changes don't lose it. Standalone/production
// is unaffected (the flag is never set there).
export const isEmbedded = () => {
  // Embedded mode requires BOTH the ?embedded=1 flag AND an actual iframe context,
  // so a standalone URL carrying the param can't hide the app's own chrome or flip
  // Auth0 token storage. reference-comparing window.top is CORS-safe (never throws).
  let inFrame = false
  try {
    inFrame = window.self !== window.top
  } catch {
    inFrame = true
  }
  let param = false
  try {
    param = new URLSearchParams(window.location.search).get("embedded") === "1"
  } catch {
    param = false
  }
  // Explicit in-frame request wins even if storage is denied (3p-cookie/partitioned
  // iframe) — persistence is best-effort, not a gate.
  if (param && inFrame) {
    try {
      sessionStorage.setItem("ct_embedded", "1")
    } catch {
      /* best-effort */
    }
    return true
  }
  // Top-level (standalone) → never embedded; clear any stale sticky flag.
  if (!inFrame) {
    try {
      sessionStorage.removeItem("ct_embedded")
    } catch {
      /* ignore */
    }
    return false
  }
  // In-frame SPA navigation that dropped the query param → honor the sticky flag.
  try {
    return sessionStorage.getItem("ct_embedded") === "1"
  } catch {
    return false
  }
}

// Whether the user may access the partner EXERCISES view (org column, org selector).
// Managers always can. A partner-entitled sponsor or trainer qualifies when their
// feature grants include an organizations grant at "managed" or "all" scope — any
// action (read or crud) suffices because exercises are org-scoped (not org-management).
//
// Intentionally broader than canManageOrgs (which requires action=crud):
//   - partner-trainer (organizations:read:managed) IS included here — exercises gate
//   - partner-trainer is EXCLUDED from canManageOrgs (read≠crud) — org management gate
// CT5 depends on trainer inclusion here. Do NOT change this to require crud.
export const canManageChildOrgs = (user) => {
  if (isManager(user)) return true
  if (!isSponsor(user) && !isTrainer(user)) return false
  return userPermissions(user).some((grant) => {
    const [feature, , scope] = String(grant).split(":")
    return feature === "organizations" && (scope === "managed" || scope === "all")
  })
}

// Whether the user may access the Organizations MANAGEMENT page/nav (R1).
// Gate: manager OR holds an `organizations` grant with action=crud and scope ∈ {managed,all}.
// Uses the PARSE pattern (feature:action:scope) — same as canManageChildOrgs.
//
// ASYMMETRY NOTE — do NOT unify with canEditScenarioOrgAccess:
//   canManageOrgs      → parse-based, crud action required; excludes partner-trainer
//                        (trainer has organizations:read:managed — read≠crud).
//   canEditScenarioOrgAccess → exact-token match for scenarios:crud:managed only;
//                        crud:all intentionally excluded (server ownership rule).
//
// Excludes partner-trainers (organizations:read:managed) — trainers get the
// Exercises/Scenarios partner views but NOT the Organizations management page.
export const canManageOrgs = (user) => {
  if (isManager(user)) return true
  return userPermissions(user).some((grant) => {
    const [feature, action, scope] = String(grant).split(":")
    return feature === "organizations" && action === "crud" && (scope === "managed" || scope === "all")
  })
}

// Whether the user may edit the org-access (limit-by-org) on a scenario.
// Gate: manager OR holds the EXACT token `scenarios:crud:managed`.
//
// Intentionally an exact-token match (NOT parse-based, NOT crud:all):
// The server's resolveScenarioOwnershipScope grants managed-children scope ONLY
// to the exact token scenarios:crud:managed. A non-manager scenarios:crud:all
// resolves to own-org only — showing the control to crud:all would surface UI
// the server will reject. Do NOT change to parse-based without a server-side audit.
//
// Includes partner-sponsor AND partner-trainer (both have scenarios:crud:managed per D1-rev2).
export const canEditScenarioOrgAccess = (user) => {
  if (isManager(user)) return true
  return userPermissions(user).includes("scenarios:crud:managed")
}

// Whether the user may edit/configure a specific scenario (D6 client-side ownership gate).
// Gate: manager → always; else scenario.ownerOrg must be non-null and present in managedOrgIds.
// managedOrgIds: array of org IDs from the actor's managed-org query
//   (/orgs for trainers — trainer-permitted; /managed-orgs for sponsors/managers).
// NOTE: server is authoritative — this only hides controls the user cannot use.
export const canEditScenarioClient = ({ user, scenario, managedOrgIds }) => {
  if (isManager(user)) return true
  const owner = scenario?.ownerOrg
  if (owner == null) return false
  // A scenario owned by the user's OWN org (e.g. locally-generated scenarios) is editable by
  // that org's admins — not only partner-managed child orgs. Cross-org (non-owned) scenarios
  // stay gated per CT-1165.
  if (owner === userOrg(user)) return true
  return Array.isArray(managedOrgIds) && managedOrgIds.includes(owner)
}

// Whether the user may delete a specific scenario.
// Gate: canEditScenarioClient AND (manager OR isSponsor).
// Trainers are excluded — the server's removeScenario action grants manager+sponsor only.
// Showing Delete to a trainer would always 403; separate from Edit to match the server grants.
export const canDeleteScenarioClient = ({ user, scenario, managedOrgIds }) => {
  return canEditScenarioClient({ user, scenario, managedOrgIds }) && (isManager(user) || isSponsor(user))
}

// Persist the user's Auth0 Organization ID so subsequent logins
// include org context (required for SSO and MFA enforcement).
const LAST_ORG_KEY = "ct_last_org_id"

export const getLastKnownOrgId = () => {
  try {
    return localStorage.getItem(LAST_ORG_KEY) || ""
  } catch {
    return ""
  }
}

export const setLastKnownOrgId = (orgId) => {
  try {
    if (orgId) {
      localStorage.setItem(LAST_ORG_KEY, orgId)
    } else {
      localStorage.removeItem(LAST_ORG_KEY)
    }
  } catch {
    // localStorage may be unavailable in some contexts
  }
}

export const clearLastKnownOrgId = () => {
  try {
    localStorage.removeItem(LAST_ORG_KEY)
  } catch {
    // noop
  }
}

// Auth provider login process may set JWT claim to "bounce" users based on risk, incomplete profile info (e.g. new sign-ups)
export const shouldBounceUser = (user) => {
  return user?.["https://www.chaostrack.com/bounce"] ?? false
}

// Canonical form for comparing email addresses: trimmed + lowercased; "" for anything that
// isn't a non-empty string. The server stores reviewers (and matches spectators) lowercased,
// while Auth0's user.email keeps whatever case the user signed up with — so every client-side
// "is this me?" email comparison must go through here (or emailsMatch), never a raw ===.
export const normalizeEmail = (email) => (typeof email === "string" ? email.trim().toLowerCase() : "")

// Case-insensitive, null-safe equality of two email addresses. Two missing/blank emails never match.
export const emailsMatch = (a, b) => {
  const na = normalizeEmail(a)
  return na !== "" && na === normalizeEmail(b)
}

// Case-insensitive "is this user's email in the list?" (e.g. session reviewers). Null-safe on
// both sides: a null/non-string list entry, a non-array list, or a user without an email never
// throws and never matches.
export const listIncludesUserEmail = ({ list, user }) => {
  const me = normalizeEmail(user?.email)
  if (!me || !Array.isArray(list)) {
    return false
  }
  return list.some((entry) => normalizeEmail(entry) === me)
}

// Case-insensitive "is this user one of the spectators?" match. Null-safe on both sides: a spectator
// row without an email, a non-string email, or a user without an email never throws and never matches.
export const spectatorsIncludeUserEmail = ({ spectators, user }) => {
  const me = normalizeEmail(user?.email)
  if (!me || !Array.isArray(spectators)) {
    return false
  }
  return spectators.some((s) => normalizeEmail(s?.email) === me)
}

export const userIdHeaderName = "Auth-Access-Id"

// Header that carries the role-assumption HS256 JWT when an assumption
// session is active. Must match the `header.name` field of the HS256
// entry in Hasura's HASURA_GRAPHQL_JWT_SECRETS, which is defined in the
// backend repo (chaos-player-poc) — see its docker-compose.base.yml.
// If this constant drifts from that config, Hasura's HS256 extractor
// stops finding the token and every assumed request silently degrades
// to the anonymous role.
//
// Per Hasura's custom-header JWT convention, the value is the raw JWT
// with NO "Bearer " prefix.
export const roleAssumptionHeaderName = "X-CT-Role-Assumption-JWT"
// Player OTP session token. A THIRD Hasura JWT_SECRETS entry reads this header; it
// must stay distinct from the two above or Hasura's multi-JWT extraction harvests two
// tokens and rejects the request before verifying anything.
export const playerSessionHeaderName = "X-CT-Player-JWT"

export const authHeadersFor = (accessToken, userId) => {
  // Header-set mutual exclusion is load-bearing: Hasura's multi-JWT
  // extraction stage iterates each configured secret entry and pulls a
  // token from its `header` source. There are now THREE entries, each on
  // its own header:
  //   IdP (Auth0/Keycloak) RS256 -> Authorization
  //   role assumption      HS256 -> X-CT-Role-Assumption-JWT
  //   player OTP session   HS256 -> X-CT-Player-JWT
  // Sending TWO of them in one request would put a token in each source,
  // the extraction list would gain two entries, and Hasura would reject
  // with "Could not verify JWT: Multiple JWTs found" (400, invalid-headers)
  // before any signature verification runs.
  //
  // Auth-Access-Id stays the real userId in every case, for rate-limit
  // attribution and audit. The assumed/player role+org context lives in the
  // HS256 token's claims, read by Hasura after verification.
  const assumptionToken = getRoleAssumption()?.token ?? null
  const playerToken = getPlayerSessionToken()

  // EXACTLY ONE token header, chosen by precedence:
  //   1. role assumption — a manager deliberately acting as someone else
  //   2. player session  — an OTP-authenticated player (no IdP session exists)
  //   3. IdP bearer      — the ordinary case
  //
  // (1) outranks (2) because the two can never legitimately coexist; if they somehow
  // did, honouring the manager's explicit action is the safer reading.
  let authHeaders
  if (assumptionToken) {
    authHeaders = { [roleAssumptionHeaderName]: assumptionToken }
  } else if (playerToken) {
    authHeaders = { [playerSessionHeaderName]: playerToken }
  } else {
    authHeaders = { Authorization: `Bearer ${accessToken}` }
  }

  authHeaders[userIdHeaderName] = userId
  return authHeaders
}

const baseURL = process.env.PUBLIC_URL ?? "/"

// TODO: Revisit this
export const normalizeRedirectURLForBasename = (url) => {
  let normURL = url
  if (normURL.startsWith(baseURL)) {
    normURL = normURL.slice(baseURL.length) ?? "/"
  }
  return normURL
}

// Auth0 URL Utils
export const urlParamsFromString = (paramString) => {
  const hashParams = new URLSearchParams(paramString)
  return Object.fromEntries([...hashParams])
}

export const decodedAuth0StateFromParamString = (paramString) => {
  let stateObj
  const params = urlParamsFromString(paramString)
  if (!params.state) {
    return null
  }
  try {
    const decBuff = Buffer.from(params.state, "base64")
    const encodedState = decBuff.toString()
    stateObj = JSON.parse(encodedState)
  } catch {
    return null
  }
  return stateObj
}

const auth0Errors = Object.freeze({
  unauthorized: "Invitation Expired or Invalid",
  access_denied: "Access Denied",
})

const auth0InvalidOrExpiredInviteLinkMessage =
  "Your invitation URL is invalid or has expired. Check your inbox for a more recent invitation URL or request access using the link below."

const auth0ErrorDescriptions = Object.freeze({
  "The verification code has expired. Please try to login again.": auth0InvalidOrExpiredInviteLinkMessage,
  "Wrong email or verification code.": auth0InvalidOrExpiredInviteLinkMessage,
  "You've reached the maximum number of attempts. Please try to login again.": auth0InvalidOrExpiredInviteLinkMessage,
  not_authorized: "Your account has not been granted access to this application. Contact your ChaosTrack administrator.",
  no_org: "Your account is not associated with an organization. Contact your ChaosTrack administrator.",
  server_error: "An error occurred during login. Please try again or contact your ChaosTrack administrator.",
})

export const shouldShowRequestNewInvitationLinkForAuth0ErrorDescription = (errorDesc) => {
  return errorDesc === auth0InvalidOrExpiredInviteLinkMessage
}

export const auth0ErrorsFromParamString = (paramString) => {
  let error = null
  let description = null
  const params = urlParamsFromString(paramString)
  error = auth0Errors[params?.error] ?? params?.error
  description = auth0ErrorDescriptions[params?.error_description] ?? params?.error_description
  return {
    error,
    description,
    showRequestNewInvitation: shouldShowRequestNewInvitationLinkForAuth0ErrorDescription(description),
  }
}

// Entitlements For Organizations, Users, etc.
export const entitlementTypes = Object.freeze({
  SCENARIO_BUILDER: "SCENARIO_BUILDER", // Access to the scenario builder + customizer features
  VOICE_COPILOT: "VOICE_COPILOT", // Spoken Voice Co-pilot ("Tracy") — mic → STT/TTS; licensable, default off
  INTEGRATIONS: "INTEGRATIONS", // Access to the Integration Hub (data-source connectors)
  BETA_FEATURES: "BETA_FEATURES", // Access to beta features (AI adversary, AI meeting facilitator, AI stakeholders)
  MULTI_LANGUAGE: "MULTI_LANGUAGE", // French/Finnish gameplay translation — off by default; hides all language UI when absent
  FACILITATED_ROUNDTABLE: "FACILITATED_ROUNDTABLE", // "Facilitated Roundtable" exercise type — default off; hides the setup card unless the org is granted it (NOT auto-granted to managers — see facilitatedRoundtableEnabled)
  KPI_ANALYTICS: "KPI_ANALYTICS", // KPI P1 (departments & groups, filtered dashboard, scenario types, exercise group tags) — slow rollout, default off; NOT auto-granted to managers (see kpiAnalyticsEnabled)
  SUB_TEAMS: "SUB_TEAMS", // Collaborative sub-teams + sub-team inject addressing (2026-10-05) — default off, granted per org; NOT auto-granted to managers (see subTeamsEnabled)
  CTF: "CTF", // Capture-the-flag exercises: CTF generation options + CTF Coordinator settings — default off, granted per org; NOT auto-granted to managers (see ctfEnabled)
})

// Customer-facing entitlements that can be toggled from the org dialogs, as a
// checkbox set (mirrors the vue admin's "Scenario Builder Access" checkbox).
// Add an entry here to surface a new entitlement toggle — the Add/Edit org
// dialogs render one checkbox per option. (ENTERPRISE_SSO is intentionally not
// here — it's managed via the SSO configuration flow, not org entitlements.)
// Scenario Builder is NOT here: it is on for every org by default (2026-10-05) — see DEFAULT_ON_ENTITLEMENTS.
export const entitlementOptions = Object.freeze([
  { key: entitlementTypes.VOICE_COPILOT, label: "Voice Co-pilot" },
  { key: entitlementTypes.INTEGRATIONS, label: "Integrations" },
  { key: entitlementTypes.BETA_FEATURES, label: "Beta Features" },
  { key: entitlementTypes.MULTI_LANGUAGE, label: "Multi-Language Support" },
  { key: entitlementTypes.FACILITATED_ROUNDTABLE, label: "Facilitated Roundtable" },
  { key: entitlementTypes.KPI_ANALYTICS, label: "KPI Analytics (departments, groups & filtered dashboard)" },
  { key: entitlementTypes.SUB_TEAMS, label: "Sub-teams (collaborative exercises)" },
  { key: entitlementTypes.CTF, label: "CTF exercises (timed, scored, leaderboard)" },
])

// Granted to every org by default — no longer toggled per org (mirrors the server's DEFAULT_ON_ENTITLEMENTS).
export const DEFAULT_ON_ENTITLEMENTS = Object.freeze([entitlementTypes.SCENARIO_BUILDER])

// The entitlement checkboxes a PARTNER admin may use on its client orgs: only those the platform admin
// enabled on the partner org (partnerGrantableEntitlements). Platform admins see every option.
// Whether a partner admin's grant list (their own partner org, fetched fresh by the org dialogs) is
// usable. pending: not loaded yet or refetching (a cached copy may be stale). failed: no org on the
// account, a failed request (getOrg resolves null), or ANY query error — even with stale cached data
// still around, which must never drive what the partner may grant. Platform admins: never either.
export const partnerGrantState = ({ userIsManager, callerOrgId, query }) => {
  if (userIsManager) return { pending: false, failed: false }
  if (!callerOrgId) return { pending: false, failed: true }
  if (query?.isError) return { pending: false, failed: true }
  if (query?.data === undefined || query?.isFetching) return { pending: true, failed: false }
  return { pending: false, failed: query.data === null }
}

export const partnerGrantableOptions = (partnerOrg) => entitlementOptions.filter((o) => partnerOrg?.partnerGrantableEntitlements?.[o.key] === true)

export const orgHasEntitlement = ({ org, entitlementType }) => {
  if (!org || !entitlementType) {
    return false
  }
  if (DEFAULT_ON_ENTITLEMENTS.includes(entitlementType)) return true

  return org?.entitlements?.[entitlementType] === true ?? false
}

export const userHasEntitlement = ({ user, org, entitlementType }) => {
  if (!entitlementType) return false
  // Managers (ChaosTrack platform admins) always have every entitlement.
  if (isManager(user)) return true
  // Default-on entitlements (Scenario Builder) belong to every customer admin.
  if (DEFAULT_ON_ENTITLEMENTS.includes(entitlementType) && (isSponsor(user) || isTrainer(user))) return true
  // Customer roles (sponsors/trainers) have an entitlement only if their org is granted it.
  if ((isSponsor(user) || isTrainer(user)) && orgHasEntitlement({ org, entitlementType })) return true
  return false
}

// KPI P1 slow-rollout gate: the org whose data is on screen holds KPI_ANALYTICS. Org-only — a
// manager viewing an org sees exactly what that org's sponsors see (the server refuses KPI
// requests for a non-entitled org with 403 KPI_FEATURE_NOT_ENABLED, managers included).
// Sub-teams (2026-10-05): org-only, like KPI — a manager acting for an org sees exactly what that org sees.
export const subTeamsEnabled = (org) => orgHasEntitlement({ org, entitlementType: entitlementTypes.SUB_TEAMS })

// CTF (capture-the-flag) exercises: org-only, like sub-teams — a manager acting for an org sees exactly what that
// org sees; the server and builder refuse enabling/changing CTF for an org without it.
export const ctfEnabled = (org) => orgHasEntitlement({ org, entitlementType: entitlementTypes.CTF })

export const kpiAnalyticsEnabled = (org) => orgHasEntitlement({ org, entitlementType: entitlementTypes.KPI_ANALYTICS })
